import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  InputLabelProps,
  MenuItem,
  Select,
  SelectProps,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ClearIcon } from "@mui/x-date-pickers";
import React from "react";
import { ISelectOption } from "../../constants/types";
import { ErrorMessage } from "../ErrorMessage/ErrorMessage";
import { CustomFormLabel } from "./CustomFormLabel";
import { FIELD_HIGHLIGHT_SX } from "../../shared";

export const CustomFormControl = styled(FormControl)(({ theme }) => ({
  /* ---------------- LABEL COLORS ---------------- */
  "& .MuiInputLabel-root": {
    fontSize: "0.8rem",
    color: "#757575",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    // Keep the default label colour on focus (no primary/blue tint).
    color: theme.palette.mode === "dark" ? "#e6e5e8" : "#5a5a5a",
  },
  "& .MuiInputLabel-root.Mui-error": {
    color: "#dc2626",
  },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: "#757575",
  },
  "& .MuiInputLabel-shrink": {
    fontSize: "0.8rem !important",
    fontWeight: "700 !important",
    transform: "translate(14px, -9px) scale(1)",
    color: "#5a5a5a",
    padding: "0 6px",
    // backgroundColor: theme.palette.mode === "dark" ? "#282C34" : "#ffffff",
    // backgroundColor: "inherit",
    // backgroundColor: "var(--parent-bg)",
    borderRadius: "3px",
  },
}));
export type CustomSelectProps = SelectProps & { highlight?: boolean };

export const CustomSelect = styled(
  (props: CustomSelectProps) => {
    const { highlight, ...rest } = props;
    return (
      <Select
        {...rest}
        MenuProps={{
          ...MenuProps,
          ...rest.MenuProps,
          PaperProps: {
            ...MenuProps.PaperProps,
            ...(rest.MenuProps?.PaperProps as any),
          },
        }}
      />
    );
  },
  {
    shouldForwardProp: (prop) => prop !== "highlight",
  },
)<CustomSelectProps>(({ theme, highlight }) => ({
  /* ---------------- INPUT ---------------- */

  // Standardise field height across all filter inputs.
  height: 38,
  "& .MuiSelect-select": {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 14,
    paddingRight: 14,
    minHeight: "38px !important",
    display: "flex",
    alignItems: "center",
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#454545",
  },

  /* ---------------- BORDER ---------------- */

  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: "5px",
    borderColor:
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#dee3e9",
  },

  /* ---------------- ICON ---------------- */

  "& .MuiSelect-icon": {
    color: "#767e89",
  },

  /* ---------------- DISABLED ---------------- */

  "& .MuiOutlinedInput-input.Mui-disabled": {
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.12)" : "#f8f9fb",
    fontWeight: 600,
    WebkitTextFillColor: "rgba(0, 0, 0, 0.55)",
  },

  /* ---------------- HIGHLIGHT BORDER & BG START ---------------- */
  ...(highlight ? FIELD_HIGHLIGHT_SX(theme) : {}),
  /* ---------------- HIGHLIGHT BORDER & BG END ---------------- */
}));

interface ICustomSelectProps extends SelectProps {
  options: { label: number | string | null; value: string | number }[];
  helperText?: string;
  label?: string;
  InputLabelProps?: InputLabelProps;
  readOnlyMode?: boolean;
  optionalData?: ISelectOption[];
  clearable?: boolean;
  onClear?: () => void;
  highlight?: boolean;
}

const CustomClearIcon = styled(ClearIcon)(({ theme }) => ({
  fontSize: "1.2rem",
}));

const ClearButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

export const ControlledCustomSelect: React.FC<ICustomSelectProps> = (props) => {
  const { sx, ...restProps } = props;
  const renderPlaceholder = () => props.placeholder;
  const handleClear = () => {
    if (props.onClear) {
      props.onClear();
    }
  };

  const getOptionValue = () => {
    if (props.multiple && Array.isArray(props.value)) {
      return props.value;
    } else if (props.multiple || props.value === "[]") {
      return [];
    } else if (props.multiple) {
      return [props.value];
    }
    return props.value || "";
  };

  return (
    <>
      <CustomFormControl fullWidth variant="outlined" size="small">
        {props.label && (
          <InputLabel
            {...props.InputLabelProps}
            sx={[
              // Highlight: fill the floating label chip and tint its text so it
              // matches the highlighted field (shared HIGHLIGHT_COLORS tokens).
              // props.highlight
              //   ? {
              //       "&.MuiInputLabel-shrink": {
              //         backgroundColor: HIGHLIGHT_COLORS.label.background.light,
              //         color: HIGHLIGHT_COLORS.label.color.light,
              //       },
              //     }
              //   : {},
              ...(Array.isArray(props.InputLabelProps?.sx)
                ? props.InputLabelProps!.sx
                : props.InputLabelProps?.sx
                  ? [props.InputLabelProps.sx]
                  : []),
            ]}
          >
            {props.label}
          </InputLabel>
        )}
        <CustomSelect
          {...restProps}
          highlight={props.highlight}
          sx={sx}
          multiple={props.multiple}
          label={props.label}
          value={getOptionValue()}
          onClose={(e: any) => {
            // Blur the select to reset label position when dropdown closes
            setTimeout(() => {
              (document.activeElement as HTMLElement)?.blur();
            }, 0);
            restProps.onClose?.(e);
          }}
          MenuProps={{
            ...restProps.MenuProps,
            PaperProps: {
              ...(restProps.MenuProps?.PaperProps as any),
              sx: {
                minWidth: "fit-content",
                maxHeight: 300,
                // Compact row spacing — matches the Handled By (Autocomplete)
                // dropdown exactly. Common to ALL ControlledCustomSelect
                // dropdowns (Source, Priority, Status, etc.).
                "& .MuiMenuItem-root": {
                  minHeight: "auto",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  fontSize: "0.8rem",
                },
                "& .MuiMenuItem-root .MuiCheckbox-root": {
                  padding: "2px",
                  marginRight: "6px",
                },
                "&::-webkit-scrollbar": {
                  width: 6,
                },
                "&::-webkit-scrollbar-thumb": {
                  borderRadius: 3,
                  backgroundColor: "rgba(0,0,0,0.2)",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(0,0,0,0.2) transparent",
                // Caller-provided PaperProps.sx (e.g. the "ALL" static option
                // styling) is merged on top of the compact defaults.
                ...((restProps.MenuProps?.PaperProps as any)?.sx || {}),
              },
            },
          }}
          renderValue={(selected) => {
            if (selected) {
              return getLabelFromValue(selected, props.options);
            }
            return renderPlaceholder();
          }}
          endAdornment={
            props.clearable && props.value ? (
              <InputAdornment position="end" sx={{ marginRight: "7px" }}>
                <ClearButton onClick={handleClear}>
                  <CustomClearIcon />
                </ClearButton>
              </InputAdornment>
            ) : null
          }
        >
          {props.options.map((option) => {
            return (
              <MenuItem key={option.value} value={option.value}>
                {props.multiple && Array.isArray(props.value) && (
                  <Checkbox checked={props.value.indexOf(option.value) > -1} />
                )}
                {option.label}
              </MenuItem>
            );
          })}
        </CustomSelect>
        {props.helperText && <ErrorMessage value={props.helperText} />}
      </CustomFormControl>
    </>
  );
};

const getLabelFromValue = (
  value: any,
  options: ICustomSelectProps["options"],
) => {
  if (Array.isArray(value)) {
    const data = options.filter((option) => value.includes(option.value));

    // If more than 2 items are selected, show only first 2 and add "+X" indicator
    if (data.length > 1) {
      const visibleItems = data.slice(0, 1);
      const remainingCount = data.length - 1;

      return (
        <Box
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            gap: 0.5,
            alignItems: "center",
          }}
        >
          {visibleItems.map((x) => (
            <Chip key={x.value} label={x.label} size="small" />
          ))}
          <Chip
            label={`+${remainingCount}`}
            size="small"
            sx={{
              backgroundColor: "#e0e0e0",
              color: "#666",
              fontWeight: "bold",
            }}
          />
        </Box>
      );
    }

    // If 2 or fewer items, show all
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {data.map((x) => (
          <Chip key={x.value} label={x.label} size="small" />
        ))}
      </Box>
    );
  }

  const option = options.find((option) => option.value === value);
  return option ? option.label : "";
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

interface ICustomSelectorProps extends Omit<SelectProps, "error"> {
  label?: string;
  placeholder?: string;
  options: string[] | number[];
  error?: string;
  readOnlyMode?: boolean;
  shrinkLabel?: string;
  InputLabelProps?: InputLabelProps;
}

export const CustomSelectorWithLabel: React.FC<ICustomSelectorProps> = (
  props,
) => {
  const {
    error,
    label,
    readOnlyMode,
    placeholder,
    shrinkLabel,
    InputLabelProps,
    ...restProps
  } = props;
  return (
    <CustomFormControl fullWidth variant="outlined" size="small" error={!!error}>
      {label && !shrinkLabel && <CustomFormLabel>{label}</CustomFormLabel>}
      {props.shrinkLabel && (
        <InputLabel {...props.InputLabelProps}>{props.shrinkLabel}</InputLabel>
      )}
      {props.readOnlyMode ? (
        <Typography variant="body1" padding={1} fontSize={"0.8rem"}>
          <>{props.value || "--"}</>
        </Typography>
      ) : (
        <CustomSelect
          {...restProps}
          fullWidth
          className={props.name}
          value={props.value}
          placeholder={placeholder}
          label={shrinkLabel}
        >
          {props.options.map((option) => {
            return (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            );
          })}
        </CustomSelect>
      )}
      {error && (
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.75rem",
            color: "#e46a76",
            marginLeft: 2,
            marginTop: 0.75,
          }}
        >
          {error}
        </Typography>
      )}
    </CustomFormControl>
  );
};
